import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { getArticleBySlug, articles } from '@/mocks/articles';

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <div className="min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100">
              <i className="ri-error-warning-line text-foreground-400 text-2xl"></i>
            </div>
            <h1 className="text-xl font-semibold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-500 mb-6">该文章可能已被移除或链接有误</p>
            <Link
              to="/articles"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap text-sm"
            >
              <i className="ri-arrow-left-line"></i>
              返回文章列表
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id && a.categorySlug === article.categorySlug)
    .slice(0, 2);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '体育资讯', href: '/articles' },
    { label: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.publishDate,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '云开体育',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <ScrollToTop />
      <Navbar />

      <section className="relative w-full h-[320px] md:h-[420px] flex items-end overflow-hidden">
        <img
          src={article.image}
          alt={article.title}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/25 to-black/60"></div>
        <div className="relative z-10 w-full px-6 md:px-8 pb-10 md:pb-12">
          <div className="max-w-3xl">
            <span className="inline-block px-2.5 py-1 rounded-md bg-primary-500/90 text-background-50 text-xs font-medium mb-3">
              {article.category}
            </span>
            <h1 className="text-xl md:text-3xl lg:text-4xl font-bold font-heading text-background-50 mb-3 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-xs text-background-50/70">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.publishDate}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                {article.readTime}
              </span>
              <span>{article.author}</span>
            </div>
          </div>
        </div>
      </section>

      <Breadcrumb items={breadcrumbItems} />

      <main className="w-full py-10 md:py-14 bg-background-50">
        <div className="w-full px-6 md:px-8">
          <div className="max-w-3xl mx-auto">
            <article
              className="prose prose-sm md:prose-base max-w-none
                prose-headings:text-foreground-900 prose-headings:font-bold prose-headings:font-heading
                prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-4
                prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
                prose-strong:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <Link
                to="/articles"
                className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                返回文章列表
              </Link>
            </div>

            {relatedArticles.length > 0 && (
              <div className="mt-12">
                <h4 className="text-lg font-bold font-heading text-foreground-900 mb-5">相关文章</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {relatedArticles.map((related) => (
                    <Link
                      key={related.id}
                      to={`/articles/${related.slug}`}
                      className="group flex gap-4 bg-background-100 rounded-xl p-4 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                    >
                      <div className="w-24 h-20 rounded-lg overflow-hidden flex-shrink-0">
                        <img
                          src={related.image}
                          alt={related.title}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h5 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors mb-1">
                          {related.title}
                        </h5>
                        <span className="text-xs text-foreground-400">{related.publishDate}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}